import { useEffect, useState } from "react";
import { Loader2, Save, User as UserIcon, Mail, Crown } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";

/**
 * Platform Admin's per-user edit dialog. Backed by
 * `PATCH /platform/users/:id` which accepts partial updates for
 * name / email / role. Active toggle is exposed as a separate control
 * on the row and is intentionally NOT surfaced here to keep the modal
 * focused on identity-fields the admin most often needs.
 */
export default function UserEditDialog({ open, onOpenChange, user, onSaved }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("user");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && user) {
      setName(user.name || "");
      setEmail(user.email || "");
      setRole(user.role || "user");
      setBusy(false);
    }
  }, [open, user]);

  if (!user) return null;

  const save = async () => {
    const payload = {};
    if ((name || "").trim() !== (user.name || "")) payload.name = name.trim();
    if ((email || "").trim().toLowerCase() !== (user.email || "").toLowerCase())
      payload.email = email.trim();
    if (role !== user.role) payload.role = role;
    if (Object.keys(payload).length === 0) {
      onOpenChange(false);
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/platform/users/${user.id}`, payload);
      toast.success("Kullanıcı güncellendi");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Güncellenemedi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={busy ? undefined : onOpenChange}>
      <DialogContent data-testid="user-edit-dialog">
        <DialogHeader>
          <DialogTitle>Kullanıcı Düzenle</DialogTitle>
          <DialogDescription>
            Ad, e-posta ve rol değişiklikleri anında geçerli olur.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="user-edit-name" className="flex items-center gap-1.5 text-xs">
              <UserIcon className="h-3.5 w-3.5 text-zinc-500" /> Ad Soyad
            </Label>
            <Input
              id="user-edit-name"
              data-testid="user-edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={busy}
              placeholder="Ad Soyad"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="user-edit-email" className="flex items-center gap-1.5 text-xs">
              <Mail className="h-3.5 w-3.5 text-zinc-500" /> E-posta
            </Label>
            <Input
              id="user-edit-email"
              type="email"
              data-testid="user-edit-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
              placeholder="user@example.com"
            />
            <p className="text-[11px] text-zinc-500">
              Değiştirirseniz kullanıcı yeni e-posta ile giriş yapmalı.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5 text-xs">
              <Crown className="h-3.5 w-3.5 text-zinc-500" /> Rol
            </Label>
            <Select value={role} onValueChange={setRole} disabled={busy}>
              <SelectTrigger data-testid="user-edit-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">user</SelectItem>
                <SelectItem value="super_admin">super_admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="user-edit-cancel"
          >
            Vazgeç
          </Button>
          <Button
            onClick={save}
            disabled={busy}
            data-testid="user-edit-save"
            className="bg-violet-600 text-white hover:bg-violet-700"
          >
            {busy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Kaydediliyor…
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" /> Kaydet
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
